import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Tag,
    Typography,
} from 'antd';
import {
    AppstoreOutlined,
    ArrowLeftOutlined,
    CheckOutlined,
} from '@ant-design/icons';

import { ICategory } from '../../../../interfaces';
import useCategoryForm from './useCategoryForm';
import PageHeader from '../../../../components/PageHeader';
import { ThemeContext } from '../../../../contexts/ThemeContext';

const { Text } = Typography;

interface IProps {
    action: 'Adicionar' | 'Editar';
    category?: ICategory;
    onSubmit: (category: ICategory) => Promise<void>;
}

export default function CategoryForm({ action, category, onSubmit }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const {
        handleSubmitForm,
        handleSubmit,
        isSubmiting,
        errors,
        control,
    } = useCategoryForm(onSubmit, category);

    const isEdit = action === 'Editar';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Breadcrumb Bar */}
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    border: `1px solid ${isDark ? '#262626' : '#f0f0f0'}`,
                    padding: '10px 16px',
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/categorias">Categorias</Link>,
                        },
                        {
                            title: isEdit ? `Editar Categoria #${category?.id ?? ''}` : 'Nova Categoria',
                        },
                    ]}
                />
            </div>

            {/* Page Header */}
            <PageHeader
                icon={<AppstoreOutlined />}
                iconBg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                title={isEdit ? `Editar Categoria: ${category?.name || ''}` : 'Nova Categoria'}
                tag={isEdit && category?.id ? <Tag color="green">ID #{category.id}</Tag> : undefined}
                subtitle={
                    isEdit
                        ? 'Atualize as informações de identificação e descrição desta categoria de recompensa'
                        : 'Cadastre uma nova categoria para classificar e organizar os produtos de resgate'
                }
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/categorias')}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Categorias
                    </Button>
                }
            />

            {/* Main Form Card */}
            <Card
                style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
                    background: isDark ? '#141414' : '#ffffff',
                    boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                }}
                bodyStyle={{ padding: '24px' }}
            >
                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                            }}
                        >
                            <AppstoreOutlined style={{ color: '#10b981', fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? '#f0f0f0' : '#1e293b', display: 'block' }}>
                                    Identificação da Categoria
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Nome e descrição para agrupamento das recompensas no aplicativo
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={14}>
                                <Form.Item
                                    label={<strong>Nome da Categoria</strong>}
                                    required
                                    validateStatus={errors.name ? 'error' : undefined}
                                    help={errors.name?.message}
                                >
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, digite o nome da categoria!' },
                                            minLength: { value: 2, message: 'O nome deve ter pelo menos 2 caracteres.' },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: Eletrônicos, Vestuário, Cupons, Utilidades"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<AppstoreOutlined style={{ color: '#10b981' }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item
                                    label={<strong>Descrição</strong>}
                                    required
                                    validateStatus={errors.description ? 'error' : undefined}
                                    help={errors.description?.message}
                                >
                                    <Controller
                                        name="description"
                                        control={control}
                                        rules={{
                                            required: { value: true, message: 'Por favor, digite a descrição da categoria!' },
                                        }}
                                        render={({ field }) => (
                                            <Input.TextArea
                                                {...field}
                                                placeholder="Descreva os tipos de recompensas ou itens englobados por esta categoria..."
                                                rows={3}
                                                style={{ borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider style={{ margin: '20px 0 16px 0' }} />

                    {/* Bottom Action Buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                        <Button
                            onClick={() => navigate('/categorias')}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 500,
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<CheckOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            {isEdit ? 'Salvar Alterações' : 'Cadastrar Categoria'}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
